"use client";

import { useState, useEffect } from "react";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import { infoCenterApi } from "@/api/query/info-center";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { FaFacebook, FaLinkedin, FaTelegram, FaWhatsapp } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { CheckCircle } from "lucide-react";

interface InfoDetail {
  id: number;
  slug: string;
  infoType: string;
  infoName: string;
  infoMaterial: string;
  infoEnum?: any;
}

export default function InfoDetailPageClient() {
  const params = useParams();
  const router = useRouter();
  const slug = params.slug as string;
  const [item, setItem] = useState<InfoDetail | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [material, setMaterial] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);

        const infoTypes = ["vacancy", "events", "news", "others"];
        let foundItem: InfoDetail | null = null;

        for (const type of infoTypes) {
          const items = await infoCenterApi.getAllInfoCenter(type);
          const found = items.find((i: any) => {
            const itemSlug = i.slug ? String(i.slug) : String(i.id);
            return itemSlug === slug;
          });

          if (found) {
            foundItem = found;
            break;
          }
        }

        if (!foundItem) {
          setError("Information not found");
          return;
        }

        setItem(foundItem);

        // ↓ Fetch full material from new endpoint using the item's id
        try {
          const fullMaterial = await infoCenterApi.getInfoCenterMaterial(
            foundItem.id,
          );
          setMaterial(fullMaterial);
        } catch (err) {
          console.error(
            "Error loading material, falling back to infoMaterial:",
            err,
          );
          setMaterial(foundItem.infoMaterial); // fallback to list data
        }

        // Fetch image
        try {
          const blob = await infoCenterApi.getInfoCenterFilePics(foundItem.id);
          setImageUrl(URL.createObjectURL(blob));
        } catch (err) {
          console.error("Error loading image:", err);
        }
      } catch (err) {
        console.error("Error fetching detail:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load information",
        );
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchDetail();

    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [slug]);

  const shareUrl =
    typeof window !== "undefined"
      ? `https://www.laplagemetaverse.org/info-center/${slug}`
      : "";
  const shareText = item
    ? `Check out: ${item.infoEnum?.infoName || item.infoName}`
    : "";

  const handleShare = (platform: string) => {
    let url = "";

    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl,
        )}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl,
        )}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + shareUrl,
        )}`;
        break;
      case "telegram":
        url = `https://t.me/share/url?url=${encodeURIComponent(
          shareUrl,
        )}&text=${encodeURIComponent(shareText)}`;
        break;
    }

    if (url) {
      window.open(url, "_blank", "width=600,height=400");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 1500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <svg
            className="w-8 h-8 animate-spin text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{error || "Not Found"}</h1>
          <Link href="/">
            <button className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main>
      <Header />
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Link href="/info-center">
            <button className="mb-6 inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-gray-700">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
          </Link>

          <div className="overflow-hidden border border-gray-200 rounded-lg">
            {imageUrl && (
              <div className="w-full h-auto overflow-hidden bg-gray-100">
                <img
                  src={imageUrl}
                  alt={item?.infoEnum?.infoName}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-6 md:p-10">
              <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                <span className="inline-block uppercase px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm font-medium">
                  {item?.infoEnum?.infoName}
                </span>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-3 text-sm">
                  <span className="text-lg font-medium">Share:</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleCopyLink}
                      className="text-gray-700 hover:text-gray-900 transition-colors underline font-medium"
                    >
                      {copySuccess ? "✓ Link Copied!" : "Copy Link"}
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleShare("facebook")}
                      className="text-[#1877F2] hover:text-[#0c5ec7] transition-colors underline font-medium"
                    >
                      <FaFacebook />
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleShare("whatsapp")}
                      className="text-[#25D366] hover:text-[#1da851] transition-colors underline font-medium"
                    >
                      <FaWhatsapp />
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleShare("telegram")}
                      className="text-[#0088cc] hover:text-[#006699] transition-colors underline font-medium"
                    >
                      <FaTelegram />
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleShare("linkedin")}
                      className="text-[#0A66C2] hover:text-[#084d8f] transition-colors underline font-medium"
                    >
                      <FaLinkedin />
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleShare("twitter")}
                      className="text-[#000000] hover:text-[#333333] transition-colors underline font-medium"
                    >
                      <FaXTwitter />
                    </button>
                  </div>
                </div>
              </div>

              <div className="prose prose-sm max-w-none text-gray-900">
                <div dangerouslySetInnerHTML={{ __html: material || "" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <section className="py-16 px-4 bg-[#21205A] min-h-[60vh] flex items-center">
        <div className="max-w-6xl mx-auto text-center">
          <h1
            className="font-bold text-xl md:text-3xl mb-6"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            Ready to Transform Your Future?
          </h1>
          <p className="text-xl text-[#FFF] mb-8 max-w-3xl mx-auto leading-relaxed">
            Join over 1.5 million graduates who have advanced their careers with
            La Plage Meta Verse. Start your learning journey today and unlock
            endless possibilities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                router.push(`https://student.laplagemetaverse.org/sign-in`);
              }}
              className="bg-[#FFF] text-[#21205A] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Browse Courses
            </button>
            <button
              onClick={() => {
                router.push(`https://student.laplagemetaverse.org/sign-up`);
              }}
              className="border-2 border-[#FFF] text-[#FFF] px-8 py-3 rounded-lg font-semibold hover:bg-[#FFF] hover:text-[#21205A] transition-colors"
            >
              Get Started Free
            </button>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-8 text-[#FFF] text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Quality Certifications</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Instant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Flexible Learning</span>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
